import { Stack, Typography } from "@mui/material";

import React, { useState } from "react";
import CaptionAndFilter from "./component/CaptionAndFilter";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import { useCustomQuery } from "store/hooks/react-query/query/useQuery";
import { VITAL_SIGNS_GRAPH } from "utils/reactQueryKeys";
import { useSanitizeQueryParams } from "utils/sanitizeQuery";
import { useParams } from "react-router-dom";
import CustomLoader from "components/atoms/CustomLoader";
import CustomGraphTooltip from "./component/CustomGraphTooltip";
import CustomRefLine from "./component/CustomRefLine";

const Graph = ({ data }) => {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data?.graph ?? []}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis dataKey="xName" />
        <YAxis
          unit={data?.range?.unit}
          domain={["auto", Number(data?.range?.normal?.max || 0) + 4]}
          allowDecimals
          tickCount={10}
        />
        <Tooltip content={<CustomGraphTooltip yName="BMI" />} />
        <ReferenceLine
          y={Number(data?.range?.normal?.max ?? 0)}
          label={
            <CustomRefLine
              range={`${data?.range?.normal?.min}${data?.range?.unit} - ${data?.range?.normal?.max}${data?.range?.unit}`}
            />
          }
          stroke="#00843566"
          strokeDasharray="5 5"
        />
        <Bar dataKey="yValue" fill="#6956E5" unit={data?.range?.unit} />
        <ReferenceLine
          y={Number(data?.range?.normal?.min ?? 0)}
          label=""
          stroke="#00843566"
          strokeDasharray="5 5"
        />
      </BarChart>
    </ResponsiveContainer>
  );
};
const VITALS_CHECKED = "bmi";

function BMI() {
  const { id } = useParams();
  const [filter, setfilter] = useState({
    name: "This Month",
    value: "ThisMonth",
  });

  //get Vital signs
  const {
    data: vitalsGraph,
    isLoading,
    isError,
  } = useCustomQuery(
    [
      VITAL_SIGNS_GRAPH,
      id,
      {
        timeType: filter.value,
        vitalSign: VITALS_CHECKED,
      },
    ],
    {
      url: useSanitizeQueryParams(
        `/visit/patients/${id}/vitals-graph?timeType=${filter.value}&vitalSign=${VITALS_CHECKED}`
      ),
      method: "get",
    },
    {
      refetchOnWindowFocus: false,
    }
  );

  const handleDropdown = (item) => {
    setfilter(item);
  };
  return (
    <Stack
      spacing={4}
      sx={{
        border: {
          lg: "1px solid rgba(196, 196, 196, 0.25)",
        },
        padding: {
          xs: 0,
          lg: 3,
        },
        borderRadius: {
          xs: "none",
          lg: "12px",
        },
      }}
    >
      <CaptionAndFilter
        filter={filter}
        handleDropdown={handleDropdown}
        icon={
          <svg
            width="24"
            height="29"
            viewBox="0 0 24 29"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 6.625C13.6914 6.625 15.0625 5.25387 15.0625 3.5625C15.0625 1.87113 13.6914 0.5 12 0.5C10.3086 0.5 8.9375 1.87113 8.9375 3.5625C8.9375 5.25387 10.3086 6.625 12 6.625Z"
              fill="#6956E5"
            />
            <path
              d="M21.8984 7.49969H2.10156C1.70995 7.49969 1.33438 7.65525 1.05748 7.93216C0.780566 8.20907 0.625 8.58464 0.625 8.97625C0.625 9.36786 0.780566 9.74343 1.05748 10.0203C1.33438 10.2972 1.70995 10.4528 2.10156 10.4528H7.67313C8.05102 10.4528 8.49344 10.6218 8.74391 11.2731C9.03648 12.0295 8.8932 13.4902 8.71273 14.6216L8.47648 15.9587C8.47692 15.9622 8.47656 15.9658 8.47542 15.9691C8.47429 15.9724 8.4724 15.9754 8.46992 15.9779L6.57773 26.7409C6.50911 27.1316 6.59804 27.5336 6.82505 27.8589C7.05206 28.1842 7.39868 28.4063 7.78906 28.4767C7.98207 28.5109 8.17993 28.5063 8.37116 28.4634C8.5624 28.4204 8.74319 28.3399 8.90305 28.2265C9.06291 28.1131 9.19865 27.969 9.30239 27.8027C9.40612 27.6364 9.47579 27.4512 9.50734 27.2577L10.8149 19.7765C10.8149 19.7765 11.2344 17.9997 12 17.9997C12.7782 17.9997 13.1889 19.7765 13.1889 19.7765L14.4965 27.2643C14.5281 27.4594 14.5983 27.6463 14.7029 27.8141C14.8075 27.9819 14.9444 28.1271 15.1057 28.2414C15.267 28.3557 15.4494 28.4368 15.6424 28.4799C15.8353 28.5229 16.0349 28.5272 16.2295 28.4923C16.4241 28.4575 16.6098 28.3843 16.7759 28.2769C16.9419 28.1696 17.0849 28.0303 17.1965 27.8671C17.3081 27.7039 17.3861 27.5202 17.426 27.3266C17.4659 27.1329 17.4669 26.9333 17.4288 26.7393L15.5361 15.9762C15.5365 15.9728 15.5362 15.9693 15.5351 15.966C15.5341 15.9627 15.5324 15.9597 15.5301 15.9571L15.2933 14.62C15.1128 13.4885 14.9695 12.0278 15.2621 11.2715C15.5115 10.6218 15.9681 10.4512 16.3165 10.4512H21.8984C22.29 10.4512 22.6656 10.2956 22.9425 10.0187C23.2194 9.74179 23.375 9.36622 23.375 8.97461C23.375 8.583 23.2194 8.20743 22.9425 7.93052C22.6656 7.65361 22.29 7.49805 21.8984 7.49805V7.49969Z"
              fill="#6956E5"
            />
          </svg>
        }
        title={`Body Mass Index (BMI)`}
        subTitle={"Calculated from weight and height."}
      />
      <Stack
        sx={{
          width: {
            xs: "70vw",
            sm: "50vw",
          },
          height: {
            xs: "35vh",
            sm: "40vh",
            md: "70vh",
          },
        }}
      >
        {isLoading ? (
          <CustomLoader />
        ) : !isError ? (
          <Graph data={vitalsGraph.data} />
        ) : (
          "No data to display"
        )}
        {!isLoading && !isError && (
          <Typography
            sx={{
              fontWeight: "bold",
              fontSize: "14px",
              opacity: 0.7,
              textAlign: "center",
            }}
          >
            {filter.name}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}

export default BMI;
